import * as THREE from 'three';

export type SceneAssetStats = {
  meshCount: number;
  skinnedMeshCount: number;
  boneCount: number;
  vertexCount: number;
  triangleCount: number;
  materialCount: number;
  textureCount: number;
  textureMegapixels: number;
  morphTargetMeshCount: number;
  morphTargetCount: number;
  morphAttributeCount: number;
  morphAttributeBytes: number;
};

export type StripMorphTargetsResult = {
  strippedMeshCount: number;
  strippedMorphTargetCount: number;
  strippedMorphAttributeCount: number;
  strippedMorphAttributeBytes: number;
};

type MorphAttribute = THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
type ColorAttribute = THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
type AlphaAwareMaterial = THREE.Material & {
  alphaMap?: THREE.Texture | null;
  map?: THREE.Texture | null;
  transmission?: number;
  vertexColors?: boolean;
};

const OPAQUE_ALPHA_EPSILON = 1e-6;

type MorphAttributeStats = {
  morphTargetCount: number;
  morphAttributeCount: number;
  morphAttributeBytes: number;
};

function getGeometryVertexCount(geometry: THREE.BufferGeometry): number {
  return geometry.getAttribute('position')?.count ?? 0;
}

function getGeometryTriangleCount(geometry: THREE.BufferGeometry): number {
  const index = geometry.getIndex();
  if (index) return Math.floor(index.count / 3);

  return Math.floor(getGeometryVertexCount(geometry) / 3);
}

function getAttributeByteLength(attribute: MorphAttribute): number {
  const array =
    attribute instanceof THREE.InterleavedBufferAttribute
      ? attribute.data.array
      : attribute.array;

  return ArrayBuffer.isView(array) ? array.byteLength : 0;
}

function getMorphAttributeStats(
  geometry: THREE.BufferGeometry,
): MorphAttributeStats {
  const morphAttributeGroups = Object.values(geometry.morphAttributes);
  let morphTargetCount = 0;
  let morphAttributeCount = 0;
  let morphAttributeBytes = 0;

  for (const attributes of morphAttributeGroups) {
    morphTargetCount = Math.max(morphTargetCount, attributes.length);
    morphAttributeCount += attributes.length;

    for (const attribute of attributes) {
      morphAttributeBytes += getAttributeByteLength(attribute);
    }
  }

  return {
    morphTargetCount,
    morphAttributeCount,
    morphAttributeBytes,
  };
}

function getMaterials(
  material: THREE.Material | THREE.Material[] | undefined,
): THREE.Material[] {
  if (!material) return [];
  return Array.isArray(material) ? material : [material];
}

function getOpaqueColorAlphaValue(attribute: ColorAttribute): number {
  if (attribute.normalized) return 1;

  const array =
    attribute instanceof THREE.InterleavedBufferAttribute
      ? attribute.data.array
      : attribute.array;

  if (
    array instanceof Uint8Array ||
    array instanceof Uint8ClampedArray ||
    array instanceof Int8Array
  ) {
    return array instanceof Int8Array ? 127 : 255;
  }
  if (array instanceof Uint16Array || array instanceof Int16Array) {
    return array instanceof Int16Array ? 32767 : 65535;
  }

  return 1;
}

function isFullyOpaqueVertexColorAttribute(attribute: ColorAttribute): boolean {
  if (attribute.itemSize < 4) return true;

  const expectedOpaqueAlpha = getOpaqueColorAlphaValue(attribute);

  for (let i = 0; i < attribute.count; i += 1) {
    if (
      Math.abs(attribute.getW(i) - expectedOpaqueAlpha) > OPAQUE_ALPHA_EPSILON
    ) {
      return false;
    }
  }

  return true;
}

function hasTextureDrivenAlpha(material: AlphaAwareMaterial): boolean {
  return Boolean(material.alphaMap || material.map);
}

function hasVertexDrivenAlpha(
  material: AlphaAwareMaterial,
  geometry: THREE.BufferGeometry,
): boolean {
  if (material.vertexColors !== true) return false;

  const colorAttribute = geometry.getAttribute('color');
  if (
    !(
      colorAttribute instanceof THREE.BufferAttribute ||
      colorAttribute instanceof THREE.InterleavedBufferAttribute
    )
  ) {
    return false;
  }

  return !isFullyOpaqueVertexColorAttribute(colorAttribute);
}

function canCoerceOpaqueBlendMaterial(
  mesh: THREE.Mesh,
  material: THREE.Material,
): boolean {
  const alphaAwareMaterial = material as AlphaAwareMaterial;

  if (!material.transparent) return false;
  if (material.opacity < 1 - OPAQUE_ALPHA_EPSILON) return false;
  if (material.alphaTest > OPAQUE_ALPHA_EPSILON) return false;
  if ((alphaAwareMaterial.transmission ?? 0) > OPAQUE_ALPHA_EPSILON) {
    return false;
  }
  if (hasTextureDrivenAlpha(alphaAwareMaterial)) return false;
  if (hasVertexDrivenAlpha(alphaAwareMaterial, mesh.geometry)) return false;

  return true;
}

function maybeAddTexture(value: unknown, textures: Set<THREE.Texture>): void {
  if (value instanceof THREE.Texture) {
    textures.add(value);
    return;
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      maybeAddTexture(entry, textures);
    }
  }
}

function collectMaterialTextures(
  material: THREE.Material,
  textures: Set<THREE.Texture>,
): void {
  for (const value of Object.values(material)) {
    maybeAddTexture(value, textures);
  }

  const uniforms = (material as THREE.ShaderMaterial).uniforms;
  if (!uniforms) return;

  for (const uniform of Object.values(uniforms)) {
    maybeAddTexture(uniform?.value, textures);
  }
}

function getImageDimension(
  image: unknown,
  dimension: 'width' | 'height',
): number {
  if (!image || typeof image !== 'object') return 0;
  const candidate = image as Record<string, unknown>;
  const videoKey = dimension === 'width' ? 'videoWidth' : 'videoHeight';
  const naturalKey = dimension === 'width' ? 'naturalWidth' : 'naturalHeight';
  const value =
    candidate[dimension] ?? candidate[naturalKey] ?? candidate[videoKey];

  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function getTextureMegapixels(texture: THREE.Texture): number {
  const width = getImageDimension(texture.image, 'width');
  const height = getImageDimension(texture.image, 'height');
  if (width <= 0 || height <= 0) return 0;

  return (width * height) / 1_000_000;
}

export function collectSceneAssetStats(scene: THREE.Object3D): SceneAssetStats {
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  const stats: SceneAssetStats = {
    meshCount: 0,
    skinnedMeshCount: 0,
    boneCount: 0,
    vertexCount: 0,
    triangleCount: 0,
    materialCount: 0,
    textureCount: 0,
    textureMegapixels: 0,
    morphTargetMeshCount: 0,
    morphTargetCount: 0,
    morphAttributeCount: 0,
    morphAttributeBytes: 0,
  };

  scene.traverse((object) => {
    if (object instanceof THREE.Bone) {
      stats.boneCount += 1;
    }

    if (!(object instanceof THREE.Mesh)) return;

    stats.meshCount += 1;
    if (object instanceof THREE.SkinnedMesh) {
      stats.skinnedMeshCount += 1;
    }

    const geometry = object.geometry;
    if (geometry) {
      stats.vertexCount += getGeometryVertexCount(geometry);
      stats.triangleCount += getGeometryTriangleCount(geometry);

      const morphStats = getMorphAttributeStats(geometry);
      if (morphStats.morphTargetCount > 0) {
        stats.morphTargetMeshCount += 1;
        stats.morphTargetCount += morphStats.morphTargetCount;
        stats.morphAttributeCount += morphStats.morphAttributeCount;
        stats.morphAttributeBytes += morphStats.morphAttributeBytes;
      }
    }

    for (const material of getMaterials(object.material)) {
      materials.add(material);
      collectMaterialTextures(material, textures);
    }
  });

  stats.materialCount = materials.size;
  stats.textureCount = textures.size;
  stats.textureMegapixels = Number(
    [...textures]
      .reduce((total, texture) => total + getTextureMegapixels(texture), 0)
      .toFixed(3),
  );

  return stats;
}

export function coerceOpaqueBlendMaterials(scene: THREE.Object3D): number {
  let coercedMaterialCount = 0;

  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    for (const material of getMaterials(object.material)) {
      if (!canCoerceOpaqueBlendMaterial(object, material)) continue;

      // Some exporters mark fully opaque meshes as BLEND. Transparent meshes
      // render after the "always on top" bone overlay, so normalize them back
      // to opaque unless there is a real alpha source we still need to honor.
      material.transparent = false;
      material.depthWrite = true;
      material.needsUpdate = true;
      coercedMaterialCount += 1;
    }
  });

  return coercedMaterialCount;
}

export function stripMorphTargetsFromScene(
  scene: THREE.Object3D,
): StripMorphTargetsResult {
  const result: StripMorphTargetsResult = {
    strippedMeshCount: 0,
    strippedMorphTargetCount: 0,
    strippedMorphAttributeCount: 0,
    strippedMorphAttributeBytes: 0,
  };

  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    const geometry = object.geometry;
    if (!geometry) return;

    const morphStats = getMorphAttributeStats(geometry);
    if (morphStats.morphTargetCount === 0) return;

    geometry.morphAttributes = {};
    geometry.morphTargetsRelative = false;
    object.morphTargetInfluences = undefined;
    object.morphTargetDictionary = undefined;
    geometry.userData.cartographerMorphTargetsStripped = true;

    result.strippedMeshCount += 1;
    result.strippedMorphTargetCount += morphStats.morphTargetCount;
    result.strippedMorphAttributeCount += morphStats.morphAttributeCount;
    result.strippedMorphAttributeBytes += morphStats.morphAttributeBytes;
  });

  return result;
}
